<script lang="ts">
import { type Component, type StyleValue } from "vue";
import { FontAwesomeIcon as FontAwesomeIconComponent } from "@fortawesome/vue-fontawesome";
import type { IconDefinition as FontAwesomeIcon } from "@fortawesome/fontawesome-svg-core";
import type { IconDefinition as CustomKitFontAwesomeIcon } from "@awesome.me/kit-bda1b4ada1/icons";
import { library } from "@fortawesome/fontawesome-svg-core";

export type TypeIcon = FontAwesomeIcon | CustomIcon | CustomKitFontAwesomeIcon;

/**
 * How do I add a new custom icon?
 *   1) Create file containing its SVG in ./icon/<my-new-icon>.vue
 *   2) Add icon into CustomIcon enum.
 *   3) Add icon into CustomIconComponents.
 */

export enum CustomIcon {
  ColectioShield,
}

export const CustomIconComponents = new Map<CustomIcon, Component>([
  // [CustomIcon.chatBubble, useAsyncComponent(() => import("./icons/ChatBubble.vue"))], // Left here as an example
  [CustomIcon.ColectioShield, useAsyncComponent(() => import("./icons/ColectioShield.vue"))],
]);

export const iconProperty = {
  // Number === item from our CustomIcon enum, Object === font awesome icon definition
  type: [Object, Number] as PropType<TypeIcon>,
  required: true,
};

export default {
  components: {
    FontAwesomeIconComponent,
  },
  props: {
    icon: iconProperty,
    scale: {
      type: Number,
      required: false,
    },
  },
  setup(props) {
    if (props.icon === undefined) {
      throw new Error("Should not happen: 'props.icon' is required"); // TS fix
    }

    const isFontAwesomeIcon = typeof props.icon !== "number";

    if (isFontAwesomeIcon) {
      library.add(props.icon as FontAwesomeIcon); // FontAwesomeIcon and CustomKitFontAwesomeIcon are both from @fortawesome/fontawesome-svg-core
    }

    const inlineStyles = computed(() => {
      const styles: StyleValue = {};
      if (props.scale !== undefined) {
        styles["transform"] = "scale(" + props.scale + ")";
      }
      return styles;
    });

    const fontAwesomeIconAttrs = computed(() => {
      const i = (props.icon as FontAwesomeIcon).icon;
      return {
        // Render default small width&height to prevent Safari from displaying extremely large
        // icon until the CSS is loaded & applied.
        width: i[0] / 100,
        height: i[1] / 100,
      };
    });

    return {
      isFontAwesomeIcon,
      CustomIconComponents,
      inlineStyles,
      fontAwesomeIconAttrs,
    };
  },
};
</script>

<template>
  <div class="icon" :style="inlineStyles">
    <FontAwesomeIconComponent v-if="isFontAwesomeIcon" :icon="icon as FontAwesomeIcon" v-bind="fontAwesomeIconAttrs" />
    <component :is="CustomIconComponents.get(icon as CustomIcon)" v-else />
  </div>
</template>

<style lang="scss" scoped>
.icon {
  width: 100%;
  height: 100%;
  /* stylelint-disable-next-line selector-disallowed-list -- FontAwesome/custom icon components render their own internal <svg> markup we don't control */
  svg,
  &:deep(svg) {
    fill: currentColor;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    * {
      transition: fill 0.3s ease;
    }
  }
}
</style>
